import React from "react";
import Image from "next/image";
import CustomIcon from "components/CustomIcon";
import { useDispatch } from "react-redux";
import {
  highlightMistakes,
  clearHighlight,
  highlightSentences,
} from "lib/redux/highlightSlice";
import type { SectionType, MistakeType } from "lib/redux/highlightSlice";

interface WordMistakesSectionProps {
  title: string;
  icon: string;
  mistakes: string[];
  // New props for highlighting context
  section?: SectionType;
  itemIndex?: number | null;
}

export const GrammarMistakesSection: React.FC<WordMistakesSectionProps> = ({
  title,
  icon,
  mistakes,
  section = "experience",
  itemIndex = null,
  //   mistakeType = "spelling",
}) => {
  const dispatch = useDispatch();

  // Helper function to determine if text is a word or sentence
  const isSentence = (text: string): boolean => {
    // Trim whitespace
    const trimmed = text.trim();

    // Check if it contains multiple words (has spaces)
    const hasMultipleWords = trimmed.includes(" ");

    // Check if it contains sentence-ending punctuation
    const hasSentencePunctuation = /[.!?]/.test(trimmed);

    // Check if it has more than 5 words (likely a sentence)
    const wordCount = trimmed.split(/\s+/).length;

    // It's a sentence if:
    // 1. Has multiple words AND sentence punctuation, OR
    // 2. Has more than 5 words, OR
    // 3. Contains common sentence structures (has commas, semicolons, etc.)
    return (
      (hasMultipleWords && hasSentencePunctuation) ||
      wordCount > 1 ||
      /[,;:]/.test(trimmed)
    );
  };

  // Handle hover on individual mistake item
  const handleMistakeMouseEnter = (mistake: string) => {
    if (isSentence(mistake)) {
      // Highlight entire sentence
      dispatch(
        highlightSentences({
          section,
          itemIndex,
          sentences: [mistake],
        })
      );
    } else {
      // Highlight individual word/phrase
      dispatch(
        highlightMistakes({
          section,
          itemIndex,
          mistakeType: "grammar",
          texts: [mistake],
        })
      );
    }
  };

  const handleMouseLeave = () => {
    dispatch(clearHighlight());
  };
  // console.log(mistakes);

  return (
    <div className="relative mt-4 flex flex-col gap-[8px] rounded-xl bg-[#FEE7E4] px-[10px] py-[8px] sm:gap-[10px] sm:px-[15px] sm:py-[10px] md:mt-3 lg:gap-[16px] lg:px-[20px] lg:py-[14px]">
      <div className="absolute left-4 top-[-16px] flex items-center gap-2 rounded-xl bg-white px-4 py-2 font-bold text-black shadow-md lg:left-8 lg:px-6">
        <Image
          src={`/icons/score-check/grammarMistake.svg`}
          height={24}
          width={24}
          alt="icon"
          className="h-4 w-4 lg:h-5 lg:w-5"
        />
        <span className="text-sm font-bold lg:text-base xl:text-lg">
          {title}
        </span>
      </div>
      <ul className=" mt-4 flex list-disc flex-col items-start px-[3px] marker:text-base marker:text-[#92C5FF] sm:px-[5px] sm:marker:text-lg md:mt-3 lg:marker:text-2xl">
        {mistakes.map((mistake, idx) => {
            let truncated = mistake;
            if (isSentence(mistake)) {
                const words = mistake.trim().split(/\s+/);
                truncated = words.length > 12 ? words.slice(0, 12).join(" ") + "..." : mistake;
            }
          return (
            <li
              key={idx}
              className="my-[6px] flex w-full cursor-pointer items-center gap-2 rounded-lg bg-[#FDFDFD] p-2 pl-2 text-xs font-normal text-[#B94444] transition-opacity hover:opacity-80 md:text-base lg:my-[6px] lg:gap-4 lg:text-base xl:my-[10px] xl:pl-3 "
              onMouseEnter={() => handleMistakeMouseEnter(mistake)}
              onMouseLeave={handleMouseLeave}
            >
              {isSentence(mistake) ? truncated : mistake}
            </li>
          );
        })}
      </ul>
      <div className="mt-4 flex flex-col items-center justify-center gap-1">
        <p className="text-center text-sm font-semibold text-red-600 lg:text-base">
          Mistakes can weaken your resume
        </p>
        <div className="relative h-32 w-44">
          <Image
            src="/assets/resumeScore/grammar.png"
            alt="Word mistakes illustration"
            fill
            className="w-15 h-10"
          />
        </div>
        <p className="text-center text-xs text-gray-600 lg:text-sm">
          Small errors can distract from your achievements. Make sure to
          proofread and correct them.
        </p>
      </div>
    </div>
  );
};
